import { ReactNode } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../../lib/utils';

interface KPICardProps {
  title: string;
  value: string;
  change?: number;
  changeLabel?: string;
  icon: ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  className?: string;
}

export function KPICard({
  title,
  value,
  change,
  changeLabel,
  icon,
  trend = 'neutral',
  className,
}: KPICardProps) {
  const trendColors = {
    up: 'text-success',
    down: 'text-destructive',
    neutral: 'text-muted-foreground',
  };

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;

  return (
    <div
      className={cn(
        'glass-card rounded-xl p-4 xl:p-4 sm:p-5 transition-all duration-300 hover:border-primary/30 group animate-slide-up overflow-hidden',
        className
      )}
    >
      <div className='flex items-start justify-between gap-2'>
        <div className='min-w-0 flex-1'>
          <p className='text-xs xl:text-xs sm:text-sm font-medium text-muted-foreground mb-1 leading-tight'>{title}</p>
          <p className='text-xl xl:text-xl sm:text-2xl lg:text-3xl font-bold text-foreground tracking-tight truncate' title={value}>{value}</p>
          {change !== undefined && (
            <div className={cn('flex items-center gap-1 mt-2 flex-wrap', trendColors[trend])}>
              <TrendIcon className='w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0' />
              <span className='text-xs sm:text-sm font-medium whitespace-nowrap'>
                {change > 0 ? '+' : ''}
                {change}%
              </span>
              {changeLabel && (
                <span className='text-xs text-muted-foreground'>{changeLabel}</span>
              )}
            </div>
          )}
        </div>
        <div className='flex-shrink-0 flex items-center justify-center w-10 h-10 xl:w-9 xl:h-9 sm:w-12 sm:h-12 rounded-xl bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors'>
          {icon}
        </div>
      </div>
    </div>
  );
}
